import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

export interface Recipe {
  id: string;
  recipe_id: string;
  user_id: string;
  type: 'recipe';
  recipe_name: string;
  description: string;
  ingredients: string[];
  instructions: string[];
  cuisine: string;
  diet_type: string;
  meal_type: string;
  tried: boolean;
  servings: string;
  prep_time: string;
  cook_time: string;
  total_time: string;
  nutrition_info: string;
  average_rating?: string;
  rating_count?: number;
  createdBy: string;
  establishment_type: string;
  location: string;
  user_ratings?: {
    [key: string]: number;
  };
}


interface AppCacheContextType {
  getCache: (key: string) => any;
  setCache: (key: string, data: any) => void;
  clearCache: () => void;
  recipes: Recipe[];
  isLoading: boolean;
  fetchRecipes: () => Promise<{ recipes: Recipe[], fromCache: boolean }>;
  invalidateCache: () => void;
  getRecipeById: (recipeId: string) => Recipe | undefined;
  fetchFullRecipeDetails: (recipeId: string) => Promise<Recipe>;
}

const AppCacheContext = createContext<AppCacheContextType | undefined>(undefined);

const CACHE_KEY = 'allRecipes';
const API_URL = 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields';

export const AppCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<Record<string, any>>(() => {
    const storedCache = sessionStorage.getItem('appCache');
    return storedCache ? JSON.parse(storedCache) : {};
  });
  const [recipes, setRecipes] = useState<Recipe[]>(() => cache[CACHE_KEY] || []);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRecipesFromAPI = useCallback(async (): Promise<Recipe[]> => {
    try {
      const response = await axios.post(API_URL, {
        entity: 'RecipeTable',
        action: 'scan',
        limit: 1000,
      }, { headers: { 'Content-Type': 'application/json' } });

      const parsedResponse = JSON.parse(response.data.body);
      return parsedResponse.data || [];
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw new Error('Failed to fetch recipes. Please try again later.');
    }
  }, []);

  const parseJsonField = useCallback((field: string | string[]): string[] => {
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing JSON field:', error);
      return [];
    }
  }, []);

  const processRecipe = useCallback((recipe: Recipe): Recipe => {
    return {
      ...recipe,
      ingredients: parseJsonField(recipe.ingredients),
      instructions: parseJsonField(recipe.instructions),
    };
  }, [parseJsonField]);

  const getCache = useCallback((key: string) => cache[key], [cache]);

  const setCacheValue = useCallback((key: string, data: any) => {
    setCache(prevCache => {
      const newCache = { ...prevCache, [key]: data };
      sessionStorage.setItem('appCache', JSON.stringify(newCache));
      return newCache;
    });
  }, []);

  const clearCache = useCallback(() => {
    setCache({});
    setRecipes([]);
    sessionStorage.removeItem('appCache');
  }, []);

  const fetchRecipes = useCallback(async (): Promise<{ recipes: Recipe[], fromCache: boolean }> => {
    if (recipes.length > 0) {
      return { recipes, fromCache: true };
    }

    try {
      const fetchedRecipes = await fetchRecipesFromAPI();
      const processedRecipes = fetchedRecipes.map(processRecipe);
      setRecipes(processedRecipes);
      setCacheValue(CACHE_KEY, processedRecipes);
      return { recipes: processedRecipes, fromCache: false };
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw new Error('Failed to fetch recipes. Please try again later.');
    }
  }, [fetchRecipesFromAPI, processRecipe, recipes, setCacheValue]);

  const invalidateCache = useCallback(() => {
    setRecipes([]);
    setCacheValue(CACHE_KEY, null);
  }, [setCacheValue]);

  const getRecipeById = useCallback((recipeId: string): Recipe | undefined => {
    return recipes.find(recipe => recipe.recipe_id === recipeId);
  }, [recipes]);

  const fetchFullRecipeDetails = useCallback(async (recipeId: string): Promise<Recipe> => {
    const cachedRecipe = getRecipeById(recipeId);
    if (cachedRecipe && cachedRecipe.ingredients && cachedRecipe.instructions) {
      return cachedRecipe;
    }

    try {
      const response = await axios.post(API_URL, {
        entity: 'RecipeTable',
        action: 'read',
        pk_column: 'recipe_id',
        pk_value: recipeId,
      }, { headers: { 'Content-Type': 'application/json' } });

      const parsedResponse = JSON.parse(response.data.body);
      const fullRecipe = processRecipe(parsedResponse.data);

      setRecipes(prevRecipes => {
        const updatedRecipes = prevRecipes.map(recipe => 
          recipe.recipe_id === recipeId ? fullRecipe : recipe
        );
        setCacheValue(CACHE_KEY, updatedRecipes);
        return updatedRecipes;
      });

      return fullRecipe;
    } catch (error) {
      console.error('Error fetching full recipe details:', error);
      throw new Error('Failed to fetch full recipe details. Please try again later.');
    }
  }, [getRecipeById, processRecipe, setCacheValue]);

  useEffect(() => {
    const initializeCache = async () => {
      setIsLoading(true);
      try {
        if (recipes.length === 0) {
          const { recipes: fetchedRecipes } = await fetchRecipes();
          setRecipes(fetchedRecipes);
        }
      } catch (error) {
        console.error('Error initializing cache:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeCache();
  }, [fetchRecipes, recipes.length]);

  const contextValue = useMemo(() => ({
    getCache,
    setCache: setCacheValue,
    clearCache,
    recipes,
    isLoading,
    fetchRecipes,
    invalidateCache,
    getRecipeById,
    fetchFullRecipeDetails
  }), [getCache, setCacheValue, clearCache, recipes, isLoading, fetchRecipes, invalidateCache, getRecipeById, fetchFullRecipeDetails]);

  return (
    <AppCacheContext.Provider value={contextValue}>
      {children}
    </AppCacheContext.Provider>
  );
};

export const useAppCache = () => {
  const context = useContext(AppCacheContext);
  if (context === undefined) {
    throw new Error('useAppCache must be used within an AppCacheProvider');
  }
  return context;
};